import { useSettings } from '../store/settings';

/**
 * Tiny synthesized sound effects (Web Audio). No audio files to download and
 * decode; the AudioContext is created lazily on first user gesture.
 */
type Sfx = 'eat' | 'golden' | 'die' | 'kill' | 'streak' | 'rankUp' | 'boost' | 'turn' | 'countdown' | 'go' | 'win' | 'lose' | 'matchFound' | 'notify' | 'click' | 'combo' | 'levelUp' | 'newBest' | 'rock' | 'zap';

let ctx: AudioContext | null = null;
/** Silenced while a portal video ad plays, or while the portal asks for silence. */
let adMuted = false;
let portalMuted = false;
const adMuteListeners = new Set<() => void>();
export const setAdMuted = (on: boolean) => {
  adMuted = on;
  for (const l of adMuteListeners) l();
};
/** The portal's own mute setting: it wins over the game's sound toggles. */
export const setPortalMuted = (on: boolean) => {
  portalMuted = on;
  for (const l of adMuteListeners) l();
};
export const isAdMuted = () => adMuted || portalMuted;
/** The music player follows ad muting too. */
export const onAdMuteChange = (fn: () => void) => adMuteListeners.add(fn);
let master: GainNode | null = null;

/** The shared AudioContext (created on first use), also used by the music player. */
export function audioContext(): AudioContext | null {
  return audio();
}

function audio(): AudioContext | null {
  if (!ctx) {
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return null;
    ctx = new AC({ latencyHint: 'interactive' });
    master = ctx.createGain();
    master.gain.value = 0.35;
    master.connect(ctx.destination);
  }
  if (ctx.state === 'suspended') void ctx.resume();
  return ctx;
}

function tone(freq: number, dur: number, type: OscillatorType = 'sine', delay = 0, vol = 1, slideTo?: number) {
  const ac = audio();
  if (!ac || !master) return;
  master.gain.value = 0.42 * useSettings.getState().sfxVolume;
  const t = ac.currentTime + delay;
  const osc = ac.createOscillator();
  const g = ac.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t);
  if (slideTo) osc.frequency.exponentialRampToValueAtTime(slideTo, t + dur);
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(vol, t + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  osc.connect(g).connect(master);
  osc.start(t);
  osc.stop(t + dur + 0.02);
}

let noiseBuf: AudioBuffer | null = null;

/** A short burst of filtered white noise: the crunch in a bite, the crash in a death. */
function noise(dur: number, filter: BiquadFilterType, freq: number, delay = 0, vol = 1, freqTo?: number, q = 1) {
  const ac = audio();
  if (!ac || !master) return;
  master.gain.value = 0.42 * useSettings.getState().sfxVolume;
  if (!noiseBuf) {
    noiseBuf = ac.createBuffer(1, Math.floor(ac.sampleRate * 0.6), ac.sampleRate);
    const d = noiseBuf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  }
  const t = ac.currentTime + delay;
  const src = ac.createBufferSource();
  src.buffer = noiseBuf;
  const f = ac.createBiquadFilter();
  f.type = filter;
  f.Q.value = q;
  f.frequency.setValueAtTime(freq, t);
  if (freqTo) f.frequency.exponentialRampToValueAtTime(freqTo, t + dur);
  const g = ac.createGain();
  g.gain.setValueAtTime(vol, t);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  src.connect(f).connect(g).connect(master);
  src.start(t);
  src.stop(t + dur + 0.02);
}

/** `level` raises the pitch of combo sounds (1..5) so streaks feel like they're building. */
export function sfx(name: Sfx, level = 1): void {
  if (!useSettings.getState().sound || isAdMuted()) return;
  switch (name) {
    case 'eat': {
      // A crunchy chomp: a bright noise snap, then a quick rising "pop".
      // Slightly different pitch each bite so a long feast doesn't drone.
      const f = 520 + Math.random() * 120;
      noise(0.05, 'bandpass', 2600 + Math.random() * 800, 0, 0.55, 1200, 1.4);
      tone(f, 0.09, 'triangle', 0.012, 0.45, f * 1.9);
      tone(90, 0.06, 'sine', 0, 0.35, 60);
      break;
    }
    case 'golden':
      [784, 988, 1175, 1568].forEach((f, i) => tone(f, 0.12, 'triangle', i * 0.05, 0.4));
      break;
    case 'die':
      // Crash: a noise hit closing down, a heavy thud, and a falling "wah" as the snake breaks apart.
      noise(0.5, 'lowpass', 4000, 0, 0.7, 180, 0.8);
      tone(110, 0.3, 'sine', 0, 0.8, 38);
      tone(340, 0.55, 'sawtooth', 0.04, 0.22, 55);
      tone(255, 0.5, 'square', 0.1, 0.1, 50);
      break;
    case 'kill':
      tone(520, 0.1, 'square', 0, 0.25);
      tone(780, 0.16, 'square', 0.08, 0.25);
      break;
    case 'streak': {
      // Rises with the streak: double, triple, rampage.
      const base = 520 * 2 ** ((Math.min(level, 4) - 1) / 4);
      [1, 1.25, 1.5, 2].forEach((m, i) => tone(base * m, 0.11, 'square', i * 0.06, 0.22));
      break;
    }
    case 'boost':
      tone(180, 0.22, 'sawtooth', 0, 0.12, 520);
      break;
    case 'rankUp':
      [523, 659, 784, 1047, 1319, 1568].forEach((f, i) => tone(f, 0.24, 'triangle', i * 0.09, 0.4));
      tone(2093, 0.6, 'sine', 0.55, 0.25);
      break;
    case 'turn':
      tone(1400, 0.02, 'sine', 0, 0.08);
      break;
    case 'countdown':
      tone(520, 0.12, 'sine', 0, 0.4);
      break;
    case 'go':
      tone(880, 0.25, 'sine', 0, 0.5);
      break;
    case 'win':
      [523, 659, 784, 1047].forEach((f, i) => tone(f, 0.22, 'triangle', i * 0.11, 0.45));
      break;
    case 'lose':
      [392, 330, 262].forEach((f, i) => tone(f, 0.28, 'sine', i * 0.15, 0.35));
      break;
    case 'matchFound':
      [659, 880].forEach((f, i) => tone(f, 0.16, 'triangle', i * 0.1, 0.5));
      break;
    case 'notify':
      tone(880, 0.1, 'sine', 0, 0.3);
      tone(1320, 0.12, 'sine', 0.08, 0.25);
      break;
    case 'click':
      tone(1000, 0.03, 'sine', 0, 0.12);
      break;
    case 'combo': {
      const base = 660 * 2 ** ((level - 1) / 6);
      tone(base, 0.07, 'triangle', 0, 0.45, base * 1.5);
      tone(base * 1.5, 0.09, 'sine', 0.05, 0.25);
      break;
    }
    case 'levelUp':
      [523, 659, 784, 1047, 1319].forEach((f, i) => tone(f, 0.1, 'square', i * 0.045, 0.18));
      break;
    case 'newBest':
      [784, 988, 1175, 1568, 1976].forEach((f, i) => tone(f, 0.2, 'triangle', i * 0.08, 0.4));
      break;
    case 'rock':
      tone(140, 0.12, 'sine', 0, 0.3, 90);
      break;
    case 'zap':
      // Electric shock: buzzy crackles falling in pitch.
      [0, 0.05, 0.11, 0.18].forEach((d, i) => tone(1800 - i * 300 + Math.random() * 200, 0.06, 'sawtooth', d, 0.22, 200));
      tone(90, 0.35, 'square', 0, 0.12, 50);
      break;
  }
}

/** Call from a user gesture (first tap) so iOS allows audio later. */
export function unlockAudio(): void {
  audio();
}
